<template>
  <div class="phone">
    <header class="topbar">
      <div class="brand" aria-label="TikTok Shop">
        <span class="brand-mark"></span>
        <span class="text-dark">TikTok Shop</span>
      </div>
      <button class="search" type="button">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
          <circle cx="11" cy="11" r="7"></circle>
          <path d="m16.5 16.5 4 4"></path>
        </svg>
      </button>
    </header>

    <div class="nav">Shop Boost</div>

    <div>
      <section class="assist-card">
        <div class="product-card" :class="{ switching: isSwitching }">
          <div class="product-image" :style="productImageStyle">{{ currentProduct.icon }}</div>
          <div class="product-info">
            <div class="product-label">TikTok Shop {{ $t('随机商品') }}</div>
            <div class="product">{{ currentProduct.name }}</div>
            <div class="product-price">{{ currentProduct.price }}</div>
          </div>
        </div>
        <button
            class="assist-button"
            @click="handleAssist"
            :disabled="isEnded || isSupporting"
            type="button"
        >
          {{ assistButtonText }}
        </button>

        <h1 class="time-title">{{ $t('倒计时') }}</h1>
        <div class="time-display">
          <span class="time-value">{{ hours }}</span>
          <span class="time-separator">:</span>
          <span class="time-value">{{ minutes }}</span>
          <span class="time-separator">:</span>
          <span class="time-value">{{ seconds }}</span>
        </div>

        <p class="copy">
          {{ $t('为') }} TikTok Shop {{ $t('商户助力可提升商品曝光度') }}，{{ $t('累计助力越高推荐权重越高') }}。
        </p>
      </section>

      <section class="feed">
        <div class="feed-title">
          <span class="text-dark">🔥 {{ $t('实时助力动态') }}</span>
          <span class="live-badge">LIVE</span>
        </div>
        <div class="ticker">
          <transition-group name="feed-list" tag="div">
            <div v-for="item in feedList" :key="item.id" class="feed-row">
              <span>{{ item.user }}</span>
              <span class="status-ok">{{ item.statusText }}</span>
              <span class="status-wait">{{ item.actionText }}</span>
              <span class="amount">{{ item.amount }}</span>
            </div>
          </transition-group>
        </div>
      </section>
    </div>

    <!-- 奖励弹层 -->
    <transition name="fade">
      <div v-if="showReward" class="reward-layer" @click.self="closeReward">
        <div class="reward-pack">
          <div class="reward-shine"></div>
          <div class="reward-content">
            <p class="reward-title">TikTok Shop {{ $t('助力奖励') }}</p>
            <div class="reward-icon">¥</div>
            <p class="reward-amount"><span>$</span><b>{{ rewardAmount }}</b></p>
            <p class="reward-note">{{ $t('随机奖励金额已生成') }}</p>
            <button class="reward-close" @click="closeReward" type="button">
              {{ $t('开心收下') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { support } from '@/api/task'

// 预设一组渐变色，用于商品卡片背景
const colorPalette = [
  'linear-gradient(135deg, #25f4ee, #fe2c55 52%, #0a0c10)',
  'linear-gradient(135deg, #ffffff, #25f4ee 52%, #111827)',
  'linear-gradient(135deg, #f8fafc, #25f4ee 48%, #fe2c55)',
  'linear-gradient(135deg, #f4e7c5, #fe2c55 48%, #101317)',
  'linear-gradient(135deg, #25f4ee, #ffffff 50%, #fe2c55)',
  'linear-gradient(135deg, #e7fbff, #25f4ee 48%, #5867dd)',
  'linear-gradient(135deg, #e6e6e6, #64748b 52%, #0f172a)',
  'linear-gradient(135deg, #ffd6e8, #fe2c55 48%, #25f4ee)',
  'linear-gradient(135deg, #25f4ee, #fb7185 45%, #111827)'
]

// 预设 emoji 图标池
const iconPool = ['🎁', '⌚', '🎧', '☕', '👟', '🌬️', '📽️', '🧴', '🧳', '📱', '💻', '📷', '🔊', '🧥', '👗']

export default {
  name: 'TikTokShopAssist',
  data() {
    return {
      goodsList: [],           // 从接口获取的商品列表
      currentGoodsIndex: 0,   // 当前展示的商品索引
      remainingSeconds: 1 * 60 * 60,
      feedList: [],
      showReward: false,
      rewardAmount: '0.00',
      isSwitching: false,
      assistSuccessFlag: false,
      isSupporting: false,
      loadingGoods: true,      // 商品加载状态
      timerInterval: null,
      productSwitchInterval: null,
      feedInterval: null
    }
  },
  computed: {
    currentProduct() {
      if (this.goodsList.length === 0) {
        // 兜底默认商品
        return {
          name: '加载中...',
          price: '$0.00',
          icon: '🛍️',
          color: colorPalette[0]
        }
      }
      const goods = this.goodsList[this.currentGoodsIndex]
      // 动态生成 icon 和 color
      const icon = this.getIconForGoods(goods)
      const color = this.getColorForGoods(goods)
      return {
        name: goods.name,
        price: `$${parseFloat(goods.price).toFixed(2)}`,
        icon,
        color
      }
    },
    productImageStyle() {
      const product = this.currentProduct
      const baseGradient = 'radial-gradient(circle at 35% 28%, rgba(255, 255, 255, 0.55), transparent 18px)'
      return { background: `${baseGradient}, ${product.color}` }
    },
    hours() {
      const safe = Math.max(0, this.remainingSeconds)
      return Math.floor(safe / 3600).toString().padStart(2, '0')
    },
    minutes() {
      const safe = Math.max(0, this.remainingSeconds)
      return Math.floor((safe % 3600) / 60).toString().padStart(2, '0')
    },
    seconds() {
      const safe = Math.max(0, this.remainingSeconds)
      return (safe % 60).toString().padStart(2, '0')
    },
    assistButtonText() {
      if (this.isEnded) return this.$t('助力已结束')
      if (this.assistSuccessFlag) return this.$t('助力成功')
      return this.$t('立即助力')
    },
    isEnded() {
      return this.remainingSeconds <= 0
    }
  },
  created() {
    this.initFeedDemo()
    this.fetchGoods()          // 加载商品列表
    this.startTimers()
  },
  beforeDestroy() {
    this.clearTimers()
  },
  methods: {
    // 获取商品列表（参考 productList.vue 的 pageGoods 方法）
    async fetchGoods() {
      this.loadingGoods = true
      try {
        // 构造请求参数，参考 pageGoods 中的参数结构
        const params = {
          curPage: 1,
          pageSize: 20,
          randomMulti: parseInt(Math.random() * new Date().getTime()),
          orders: [{ column: '`storeGoods.createTime`', asc: false }], // 按最新排序
          // 可根据需要添加其他筛选条件，例如只拉取上架状态的商品，这里省略
        }
        // 使用 axios 发送 GET 请求，也可用 this.$get（如果全局注入了）
        const res = await this.$get('/goods/page', { params })
        const page = res.data
        this.goodsList = page.records || []
        if (this.goodsList.length === 0) {
          // 无商品时显示提示或使用静态占位
          console.warn('未获取到商品数据')
        }
        // 随机选择一个起始索引，避免每次都从第一个开始
        this.currentGoodsIndex = Math.floor(Math.random() * this.goodsList.length)
      } catch (error) {
        console.error('获取商品失败', error)
        // 降级：使用静态商品列表（保证页面不崩溃）
        this.goodsList = this.getFallbackGoods()
      } finally {
        this.loadingGoods = false
      }
    },
    // 兜底静态商品（原静态数据转换格式）
    getFallbackGoods() {
      return [
        { goodsName: '爆款助力礼盒', price: '129.00' },
        { goodsName: '直播热卖智能手表', price: '89.90' },
        { goodsName: '达人推荐无线耳机', price: '59.99' },
        { goodsName: 'TikTok 同款咖啡机', price: '146.35' },
        { goodsName: '短视频爆款跑鞋', price: '111.54' },
        { goodsName: '居家空气净化器', price: '93.83' },
        { goodsName: '便携迷你投影仪', price: '158.00' },
        { goodsName: '达人带货香氛套装', price: '76.12' },
        { goodsName: '旅行达人行李箱', price: '139.50' }
      ]
    },
    // 根据商品生成 icon emoji
    getIconForGoods(goods) {
      // 根据商品名称 hash 或 id 取模，使同一商品 icon 固定
      const name = goods.goodsName || ''
      let hash = 0
      for (let i = 0; i < name.length; i++) {
        hash = ((hash << 5) - hash) + name.charCodeAt(i)
        hash |= 0
      }
      const index = Math.abs(hash) % iconPool.length
      return iconPool[index]
    },
    // 根据商品生成渐变背景色
    getColorForGoods(goods) {
      // 使用商品 id 或索引确定颜色
      const id = goods.id || goods.goodsId || 0
      const index = id % colorPalette.length
      return colorPalette[index]
    },
    // 切换商品（轮播）
    switchProduct() {
      if (this.isSwitching || this.goodsList.length === 0) return
      this.isSwitching = true
      setTimeout(() => {
        // 顺序切换，循环
        let nextIndex = (this.currentGoodsIndex + 1) % this.goodsList.length
        this.currentGoodsIndex = nextIndex
        this.isSwitching = false
      }, 260)
    },
    // 倒计时更新
    updateCountdown() {
      if (this.remainingSeconds <= 0) {
        if (this.timerInterval) clearInterval(this.timerInterval)
        return
      }
      this.remainingSeconds -= 1
    },
    // 助力逻辑（原样保留）
    async handleAssist() {
      if (this.isEnded || this.isSupporting) return
      this.isSupporting = true
      try {
        const res = await support()
        const amount = res.data
        this.addFeed(true)
        this.rewardAmount = amount
        this.showReward = true
        this.assistSuccessFlag = true
        setTimeout(() => { this.assistSuccessFlag = false }, 900)
      } catch (error) {
        let msg = this.$t('助力失败')
        if (error.response && error.response.data && error.response.data.message) {
          msg = error.response.data.message
        } else if (error.message) {
          msg = error.message
        }
        alert(msg)
      } finally {
        this.isSupporting = false
      }
    },
    closeReward() {
      this.showReward = false
    },
    // 以下 feed 相关方法保持不变
    randomAmount() {
      return (Math.random() * 145 + 8).toFixed(2)
    },
    createFeedRow(forceSuccess = false) {
      const userIds = ['456', '123', '789', '216', '655', '908', '337', '501']
      const userSuffix = userIds[Math.floor(Math.random() * userIds.length)]
      const success = forceSuccess || Math.random() > 0.28
      return {
        id: Date.now() + Math.random(),
        user: `User***${userSuffix}`,
        statusText: success ? this.$t('成功') : this.$t('进行中'),
        actionText: success ? this.$t('刷新') : this.$t('等待'),
        amount: `+$${this.randomAmount()}`,
        successFlag: success
      }
    },
    addFeed(forceSuccess = false) {
      const newRow = this.createFeedRow(forceSuccess)
      this.feedList.unshift(newRow)
      if (this.feedList.length > 8) this.feedList.pop()
    },
    initFeedDemo() {
      for (let i = 0; i < 7; i++) this.addFeed(true)
    },
    startTimers() {
      this.timerInterval = setInterval(() => this.updateCountdown(), 1000)
      this.productSwitchInterval = setInterval(() => {
        if (!this.isEnded && this.goodsList.length > 0) this.switchProduct()
      }, 3500)
      this.feedInterval = setInterval(() => {
        if (!this.isEnded) this.addFeed(false)
      }, 1600)
    },
    clearTimers() {
      if (this.timerInterval) clearInterval(this.timerInterval)
      if (this.productSwitchInterval) clearInterval(this.productSwitchInterval)
      if (this.feedInterval) clearInterval(this.feedInterval)
    }
  },
  watch: {
    isEnded(newVal) {
      if (newVal) {
        this.assistSuccessFlag = false
        if (this.timerInterval) clearInterval(this.timerInterval)
      }
    }
  }
}
</script>

<style scoped>
/* 样式保持不变 */
:root {
  --bg: #080a0d;
  --panel: #101317;
  --panel-soft: #171c22;
  --line: #26303a;
  --text: #f5f7f8;
  --muted: #9aa3aa;
  --accent: #fe2c55;
  --cyan: #25f4ee;
  --success: #33f1c8;
  --blue: #25f4ee;
}

* {
  box-sizing: border-box;
}

.phone {
  width: min(100vw, 430px);
  min-height: 95vh;
  background: #FFFFFF;
  overflow: hidden;
  margin: 0 auto;
}

.topbar {
  height: 66px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px 0 13px;
  background: #FFFFFF;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.brand {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 20px;
  font-weight: 900;
  color: #fff;
}

.brand-mark {
  width: 31px;
  height: 31px;
  background: #050607;
  border-radius: 8px;
  display: grid;
  place-items: center;
  box-shadow: -3px 0 0 var(--cyan), 3px 0 0 var(--accent);
  transform: rotate(-4deg);
  position: relative;
}

.brand-mark::before {
  content: "♪";
  color: #fff;
  font-size: 23px;
  font-weight: 900;
}

.search {
  width: 29px;
  height: 29px;
  background: transparent;
  border: 0;
  color: #c8cdce;
  display: grid;
  place-items: center;
  cursor: pointer;
  padding: 0;
}

.search svg {
  width: 25px;
  height: 25px;
  stroke-width: 2.5;
  stroke: currentColor;
}

.nav {
  height: 41px;
  display: grid;
  place-items: center;
  color: #000000;
  font-size: 13px;
  font-weight: 700;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  letter-spacing: 0.5px;
}

.assist-card {
  width: min(calc(100% - 62px), 342px);
  min-height: 327px;
  margin: 0 auto;
  padding: 24px 18px 22px;
  background: linear-gradient(180deg, rgba(37, 244, 238, 0.08), rgba(254, 44, 85, 0.045) 46%, rgba(255, 255, 255, 0.02)), #101317;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.22);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.product-card {
  width: 100%;
  min-height: 112px;
  display: grid;
  grid-template-columns: 86px minmax(0, 1fr);
  align-items: center;
  gap: 14px;
  padding: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: linear-gradient(135deg, #171c22, #0f1217);
  transition: opacity 0.28s ease, transform 0.28s ease;
}

.product-card.switching {
  opacity: 0;
  transform: translateY(-5px);
}

.product-image {
  width: 86px;
  height: 86px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.16), 0 10px 22px rgba(0, 0, 0, 0.24);
  font-size: 42px;
  background-size: cover;
}

.product-info {
  min-width: 0;
}

.product-label {
  margin-bottom: 8px;
  color: #8f9699;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.3px;
}

.product {
  font-size: 19px;
  font-weight: 900;
  color: #fff;
  line-height: 1.25;
  word-break: break-word;
}

.product-price {
  margin-top: 10px;
  color: var(--success);
  font-size: 14px;
  font-weight: 900;
}

.assist-button {
  width: 100%;
  height: 45px;
  margin: 17px 0 28px;
  border: 1px solid rgba(254, 44, 85, 0.58);
  border-radius: 6px;
  background: linear-gradient(90deg, #25f4ee, #ffffff 48%, #fe2c55);
  color: #090b0f;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 10px 28px rgba(254, 44, 85, 0.16), 0 0 0 3px rgba(37, 244, 238, 0.05);
  transition: transform 0.08s linear;
}

.assist-button:active {
  transform: translateY(1px);
}

.assist-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.time-title {
  margin: 0 0 14px;
  font-size: 20px;
  font-weight: 900;
  color: var(--text);
}

.time-display {
  width: 100%;
  height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-bottom: 18px;
  border-radius: 8px;
  background: linear-gradient(135deg, #151a20, #0c0f13);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.025);
}

.time-value,
.time-separator {
  color: #fff;
  font-size: 27px;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
}

.time-value {
  width: 43px;
  text-align: center;
}

.time-separator {
  width: 8px;
  transform: translateY(-2px);
}

.copy {
  width: 100%;
  color: #f6f7f8;
  font-size: 13px;
  line-height: 1.45;
  font-weight: 800;
  text-align: center;
  opacity: 0.9;
}

.feed {
  margin-top: 20px;
  padding: 11px 20px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.feed-title {
  height: 28px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 900;
  margin-bottom: 10px;
}

.live-badge {
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(254, 44, 85, 0.2);
  color: #ffb5c3;
  font-size: 9px;
  font-weight: 900;
}

.ticker {
  max-height: 200px;
  overflow-y: auto;
  padding-right: 4px;
  scrollbar-width: thin;
}

.ticker::-webkit-scrollbar {
  width: 3px;
  background: #1e2a32;
  border-radius: 10px;
}

.ticker::-webkit-scrollbar-thumb {
  background: #fe2c55;
  border-radius: 10px;
}

.feed-row {
  height: 34px;
  display: grid;
  grid-template-columns: minmax(84px, 1fr) 42px 43px auto;
  align-items: center;
  gap: 6px;
  border-bottom: 1px solid #20333e;
  color: #b5bdc1;
  font-size: 12px;
  font-weight: 800;
  text-shadow: 0 0 2px rgba(255, 255, 255, 0.2);
  padding: 0 4px;
}

.status-ok {
  color: var(--blue);
  font-size: 11px;
}

.status-wait {
  color: #909698;
  font-size: 11px;
}

.amount {
  justify-self: end;
  color: var(--success);
  font-size: 13px;
  font-weight: 900;
  white-space: nowrap;
}

.reward-layer {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(6px);
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.reward-pack {
  width: min(315px, 100%);
  position: relative;
  overflow: hidden;
  padding: 27px 22px 22px;
  border-radius: 12px;
  background: radial-gradient(circle at 50% -4%, rgba(255, 237, 153, 0.55), transparent 70px),
  linear-gradient(180deg, #181d24, #0d1015 56%, #080a0d);
  border: 1px solid rgba(37, 244, 238, 0.34);
  box-shadow: 0 26px 58px rgba(0, 0, 0, 0.48), inset 0 0 0 1px rgba(255, 255, 255, 0.11);
  text-align: center;
  animation: packIn 0.26s ease;
}

.reward-pack::before,
.reward-pack::after {
  content: "";
  position: absolute;
  left: -18%;
  right: -18%;
  height: 72px;
  border-radius: 0 0 50% 50%;
  background: rgba(254, 44, 85, 0.18);
}

.reward-pack::before {
  top: 84px;
}

.reward-pack::after {
  top: 92px;
  border-top: 1px solid rgba(37, 244, 238, 0.24);
  background: transparent;
}

.reward-shine {
  position: absolute;
  inset: -90px -40px auto;
  height: 190px;
  background: conic-gradient(from 0deg, transparent, rgba(255, 242, 185, 0.45), transparent 28%);
  opacity: 0.42;
  animation: shineSpin 4s linear infinite;
}

.reward-content {
  position: relative;
  z-index: 1;
}

.reward-title {
  margin: 0 0 16px;
  color: #ffffff;
  font-size: 20px;
  font-weight: 900;
}

.reward-icon {
  width: 74px;
  height: 74px;
  margin: 0 auto 12px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: linear-gradient(135deg, #25f4ee, #ffffff 48%, #fe2c55);
  color: #090b0f;
  font-size: 35px;
  font-weight: bold;
  box-shadow: 0 12px 24px rgba(92, 0, 0, 0.28), inset 0 0 0 4px rgba(255, 255, 255, 0.25);
}

.reward-amount {
  margin: 0;
  color: #25f4ee;
  font-size: 43px;
  line-height: 1;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 4px 12px rgba(70, 0, 0, 0.36);
}

.reward-amount span {
  font-size: 21px;
}

.reward-note {
  margin: 10px 0 18px;
  color: rgba(255, 255, 255, 0.86);
  font-size: 13px;
  font-weight: 800;
}

.reward-close {
  width: 100%;
  height: 43px;
  border: 0;
  border-radius: 7px;
  background: linear-gradient(90deg, #25f4ee, #ffffff 48%, #fe2c55);
  color: #090b0f;
  font-size: 15px;
  font-weight: 900;
  cursor: pointer;
  box-shadow: 0 9px 20px rgba(84, 0, 0, 0.24);
}

@keyframes packIn {
  from {
    opacity: 0;
    transform: scale(0.92) translateY(12px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes shineSpin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 380px) {
  .assist-card {
    width: calc(100% - 48px);
  }
  .feed {
    padding-left: 16px;
    padding-right: 16px;
  }
  .feed-row {
    gap: 4px;
    font-size: 11px;
  }
}
</style>